/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { components as selectComponents } from 'react-select';
import type {
  GroupBase,
  OptionProps,
  SingleValueProps,
  StylesConfig,
} from 'react-select';
import { css, useTheme } from '@emotion/react';
import {
  BirdiTheme,
  buttons,
  forms,
  buttonBlue,
} from '@birdi/theme';
import { closeButtonCSS, contentCSS, portalCSS } from '@birdi/modal-kit';
import CloseSVG from '@birdi/icons/close.svg';
import UploadSVG from '@birdi/icons/upload.svg';
import MapSVG from '@birdi/icons/map.svg';
import FolderSVG from '@birdi/icons/folder.svg';
import {
  mapFolders,
  maps as mapsAPI,
  organisation as organisationAPI,
  stroot,
} from '@birdi/js-sdk';
import type { MemberWorkspaceSummary } from '@birdi/js-sdk/src/public/organisation';
import { InformationIndicator } from '@birdi/components-common';
import { useMetaSelector } from '../../meta/reducer';

const INFO_TOOLTIP =
  'Create a new map to upload data, or add files to a map you already have.';

const PROCESS_DEST_INFO_TOOLTIP =
  'Upload a new dataset to process, or pick a map folder that already contains georeferenced imagery.';

const PROCESS_FOLDER_INFO_TOOLTIP =
  'Only folders that directly contain at least one georeferenced image layer can be processed (same rule as the map layer menu).';

const PROCESS_MERGE_HINT_BOX =
  "If the selected folder doesn't contain all required drone images, open the map and merge the folders to combine the imagery.";

/** Fallback: portaled menus (if any) sit outside Dialog.Content; block dismiss-layer handling. */
function allowReactSelectMenuInteraction(e: Event) {
  const t = e.target;
  if (!(t instanceof Element)) return;
  if (
    t.closest('[role="listbox"]') ||
    t.closest('[role="option"]') ||
    t.closest('[class*="MenuList"]') ||
    t.closest('[class*="MenuPortal"]') ||
    t.closest('[id^="react-select-"]')
  ) {
    e.preventDefault();
  }
}

type LabeledOption = { value: string; label: string };

/** Existing-map picker: id + name only (noCover API; no thumbnail/location in UI). */
type MapSelectOption = {
  value: string;
  label: string;
};

const MAP_SEARCH_PAGE_SIZE = 20;

export type UploadMapDestinationMode = 'create' | 'existing';

export type UploadMapDestinationResult =
  | {
      kind: 'create';
      mapName: string;
      organisationId: string;
      folderId: string | null;
    }
  | { kind: 'existing'; mapId: string; layerFolderId?: string };

export interface UploadMapDestinationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: UploadMapDestinationMode | null;
  /** Dashboard “Upload to a map” vs “Process a map” — changes copy and existing-map steps. */
  flowVariant?: 'upload' | 'process';
  onBack: () => void;
  onContinue?: (result: UploadMapDestinationResult) => void;
  /**
   * Hide the "Change" (back to chooser) button. Used by the site page, where
   * the flow is create-a-new-map only — switching to "add to existing" there
   * wouldn't attach the map to the site (and the map may already have one).
   */
  hideChangeButton?: boolean;
}

/** Selected + hover/focus use lightOcean and dark text (not default react-select primary). */
const modalSelectOptionStyle = (theme: BirdiTheme) =>
  function optionStyle(base: Record<string, unknown>, state: { isFocused: boolean; isSelected: boolean }) {
    return {
      ...base,
      backgroundColor:
        state.isFocused || state.isSelected ? theme.lightOcean : theme.mono0,
      color: theme.midnight,
      cursor: 'pointer',
    };
  };

const selectStyles = (
  theme: BirdiTheme,
): StylesConfig<LabeledOption, false, GroupBase<LabeledOption>> => ({
  ...(forms.reactSelect as StylesConfig<
    LabeledOption,
    false,
    GroupBase<LabeledOption>
  >),
  menuPortal: (base) => ({
    ...base,
    zIndex: 10001,
  }),
  container: (base) => ({
    ...base,
    width: '100%',
  }),
  control: (base) => ({
    ...base,
    borderRadius: '8px',
    border: `1px solid ${theme.mono30}`,
    minHeight: '44px',
  }),
  /** In-dialog menu (no portal): keep above fields inside the modal stacking context */
  menu: (base) => ({
    ...base,
    zIndex: 20,
  }),
  option: modalSelectOptionStyle(theme),
});

const mapSelectStyles = (
  theme: BirdiTheme,
): StylesConfig<MapSelectOption, false, GroupBase<MapSelectOption>> => ({
  ...(forms.reactSelect as StylesConfig<
    MapSelectOption,
    false,
    GroupBase<MapSelectOption>
  >),
  menuPortal: (base) => ({
    ...base,
    zIndex: 10001,
  }),
  container: (base) => ({
    ...base,
    width: '100%',
  }),
  control: (base) => ({
    ...base,
    borderRadius: '8px',
    border: `1px solid ${theme.mono30}`,
    minHeight: '44px',
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
  }),
  option: modalSelectOptionStyle(theme),
});

export const UploadMapDestinationModal: React.FC<
  UploadMapDestinationModalProps
> = ({
  open,
  onOpenChange,
  mode,
  flowVariant = 'upload',
  onBack,
  onContinue,
  hideChangeButton = false,
}) => {
  const theme = useTheme() as BirdiTheme;
  const meta = useMetaSelector();

  const [mapName, setMapName] = useState('');
  const [workspace, setWorkspace] = useState<LabeledOption | null>(null);
  const [folder, setFolder] = useState<LabeledOption | null>(null);
  const [workspaceOptions, setWorkspaceOptions] = useState<LabeledOption[]>([]);
  const [folderOptions, setFolderOptions] = useState<LabeledOption[]>([]);
  const [mainOrganisationId, setMainOrganisationId] = useState<string | null>(
    null,
  );
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);

  const [selectedMap, setSelectedMap] = useState<MapSelectOption | null>(null);

  const [processFolderOptions, setProcessFolderOptions] = useState<
    LabeledOption[]
  >([]);
  const [selectedProcessFolder, setSelectedProcessFolder] =
    useState<LabeledOption | null>(null);
  const [processFoldersLoading, setProcessFoldersLoading] = useState(false);

  const currentContextOrgId =
    meta.workspace?.id ?? meta.organisation.id ?? null;

  const reset = useCallback(() => {
    setMapName('');
    setWorkspace(null);
    setFolder(null);
    setWorkspaceOptions([]);
    setFolderOptions([]);
    setMainOrganisationId(null);
    setSelectedMap(null);
    setProcessFolderOptions([]);
    setSelectedProcessFolder(null);
  }, []);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && mode && !wasOpenRef.current) {
      reset();
    }
    wasOpenRef.current = open;
  }, [open, mode, reset]);

  useEffect(() => {
    if (!open || mode !== 'create' || !meta.loaded) return;
    let cancelled = false;
    (async () => {
      setWorkspacesLoading(true);
      try {
        const sessionScopeId = (meta as { scopeId?: string }).scopeId;
        if (!sessionScopeId) {
          if (!cancelled) {
            setWorkspaceOptions([]);
            setMainOrganisationId(meta.organisation?.id ?? null);
            setWorkspace(null);
          }
          return;
        }
        const wsRes = await organisationAPI.getMyWorkspaces(stroot('thuw8a'), {
          contextScopeId: sessionScopeId,
          includeMenuIcon: false,
        });
        const items: MemberWorkspaceSummary[] = wsRes.body?.items ?? [];
        const mainId =
          items.find((w) => !w.primaryOrgId)?.id ??
          items[0]?.primaryOrgId ??
          meta.organisation?.id ??
          null;
        if (cancelled) return;
        setMainOrganisationId(mainId);
        const options = items
          .filter((w) => w.id && w.name)
          .map((w) => ({ value: w.id, label: w.name }));
        setWorkspaceOptions(options);

        const ctxId = meta.workspace?.id ?? meta.organisation?.id ?? null;
        const preselected =
          (ctxId ? options.find((o) => o.value === ctxId) : undefined) ??
          (mainId ? options.find((o) => o.value === mainId) : undefined) ??
          options[0] ??
          null;
        setWorkspace(preselected);
      } catch {
        if (!cancelled) {
          setWorkspaceOptions([]);
          setMainOrganisationId(meta.organisation?.id ?? null);
          setWorkspace(null);
        }
      } finally {
        if (!cancelled) setWorkspacesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    mode,
    meta.loaded,
    meta.organisation?.id,
    meta.workspace?.id,
    (meta as { scopeId?: string }).scopeId,
  ]);

  useEffect(() => {
    if (!open || mode !== 'create' || !workspace?.value) {
      setFolderOptions([]);
      setFolder(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setFoldersLoading(true);
      try {
        const res = await mapFolders.getFolders(stroot('umdst3'), workspace.value);
        if (cancelled) return;
        setFolderOptions(
          (res.body ?? []).map((f) => ({ value: f.id, label: f.name })),
        );
      } catch {
        if (!cancelled) setFolderOptions([]);
      } finally {
        if (!cancelled) setFoldersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, workspace]);

  const loadMapOptions = useCallback(
    async (inputValue: string): Promise<MapSelectOption[]> => {
      if (!currentContextOrgId) return [];
      try {
        const q = inputValue.trim();
        const res = await mapsAPI.getOrganisationMaps(
          stroot('umdst4'),
          currentContextOrgId,
          {
            start: 1,
            limit: MAP_SEARCH_PAGE_SIZE,
            noCover: 'yes',
            ...(q ? { mapName: q } : {}),
          },
        );
        const maps = res.body ?? [];
        return maps.map((m) => ({
          value: m.id,
          label: m.name || 'Untitled',
        }));
      } catch {
        return [];
      }
    },
    [currentContextOrgId],
  );

  const MapSearchOptionRow = (props: OptionProps<MapSelectOption, false>) => (
    <selectComponents.Option {...props}>
      <div
        css={css`
          padding: 0.15em 0;
          font-weight: 600;
          color: ${theme.midnight};
        `}
      >
        {props.data.label}
      </div>
    </selectComponents.Option>
  );

  const MapSearchSingleValue = (
    props: SingleValueProps<MapSelectOption, false>,
  ) => (
    <selectComponents.SingleValue {...props}>
      <span
        css={css`
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `}
      >
        {props.data.label}
      </span>
    </selectComponents.SingleValue>
  );

  useEffect(() => {
    if (!open || mode !== 'existing' || flowVariant !== 'process') {
      setProcessFolderOptions([]);
      setSelectedProcessFolder(null);
      return;
    }
    if (!selectedMap?.value) {
      setProcessFolderOptions([]);
      setSelectedProcessFolder(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setProcessFoldersLoading(true);
      try {
        const res = await mapsAPI.getProcessingEligibleFolders(
          stroot('oy4aip'),
          selectedMap.value,
        );
        if (cancelled) return;
        const items = res.body?.items ?? [];
        setProcessFolderOptions(
          items.map((it) => ({ value: it.id, label: it.pathLabel })),
        );
        setSelectedProcessFolder(null);
      } catch {
        if (!cancelled) {
          setProcessFolderOptions([]);
          setSelectedProcessFolder(null);
        }
      } finally {
        if (!cancelled) setProcessFoldersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, flowVariant, selectedMap?.value]);

  const folderSelectEnabled = Boolean(workspace?.value);

  const canContinueCreate =
    mode === 'create' &&
    !workspacesLoading &&
    mapName.trim().length > 0 &&
    !!(workspace?.value ?? mainOrganisationId);

  const canContinueExisting =
    mode === 'existing' &&
    (flowVariant !== 'process' || !processFoldersLoading) &&
    Boolean(selectedMap?.value) &&
    (flowVariant !== 'process' || Boolean(selectedProcessFolder?.value));

  const canContinue =
    mode === 'create' ? canContinueCreate : canContinueExisting;

  const sectionTitle =
    mode === 'create'
      ? 'Create a new map'
      : mode === 'existing'
        ? flowVariant === 'process'
          ? 'Process existing map data'
          : 'Add to existing map'
        : '';

  const dialogTitle =
    flowVariant === 'process' ? 'Process map data' : 'Upload to a map';
  const dialogInfoTooltip =
    flowVariant === 'process' ? PROCESS_DEST_INFO_TOOLTIP : INFO_TOOLTIP;
  const DialogTitleIcon = flowVariant === 'process' ? MapSVG : UploadSVG;
  const continueLabel =
    flowVariant === 'process' && mode === 'existing'
      ? 'Continue to process'
      : 'Continue to upload';
  const ContinueIcon = UploadSVG;

  const handleContinue = () => {
    if (!canContinue || !mode) return;
    if (mode === 'create') {
      const organisationId = workspace?.value ?? mainOrganisationId!;
      onContinue?.({
        kind: 'create',
        mapName: mapName.trim(),
        organisationId,
        folderId: folder?.value ?? null,
      });
      return;
    }
    if (flowVariant === 'process') {
      onContinue?.({
        kind: 'existing',
        mapId: selectedMap!.value,
        layerFolderId: selectedProcessFolder!.value,
      });
      return;
    }
    onContinue?.({
      kind: 'existing',
      mapId: selectedMap!.value,
    });
  };

  if (!mode) return null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <div css={portalCSS}>
          <Dialog.Content
            data-testid="upload-map-destination-modal"
            onPointerDownOutside={allowReactSelectMenuInteraction}
            onInteractOutside={allowReactSelectMenuInteraction}
            css={(t) => css`
              ${contentCSS(t)};
              width: 32rem;
              max-width: 90vw;
              padding: 2em;
              padding-top: 2.25em;
              overflow: visible;
            `}
          >
            <Dialog.Close
              css={closeButtonCSS}
              data-testid="upload-map-destination-modal-close"
              aria-label="Close"
            >
              <CloseSVG />
            </Dialog.Close>

            <Dialog.Title asChild>
              <h2
                css={css`
                  margin: 0 2.5rem 0 0;
                  font-size: 1.25rem;
                  font-weight: 700;
                  color: ${theme.midnight};
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                  flex-wrap: wrap;
                `}
              >
                <span
                  css={css`
                    display: inline-flex;
                    width: 1.5rem;
                    height: 1.5rem;
                    flex-shrink: 0;
                    & svg {
                      width: 100%;
                      height: 100%;
                    }
                  `}
                >
                  <DialogTitleIcon aria-hidden="true" />
                </span>
                <span>{dialogTitle}</span>
                <InformationIndicator message={dialogInfoTooltip} />
              </h2>
            </Dialog.Title>

            <div
              css={css`
                height: 1px;
                background: ${theme.mono30};
                margin: 1rem 0 1.25rem;
              `}
            />

            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 1rem;
                margin: 0 0 0.25rem 0;
              `}
            >
              <h3
                css={css`
                  margin: 0;
                  font-size: 1.125rem;
                  font-weight: 700;
                  color: ${theme.midnight};
                `}
              >
                {sectionTitle}
              </h3>
              {!hideChangeButton && (
                <button
                  type="button"
                  data-testid="upload-map-destination-modal-change"
                  css={css`
                    padding: 0.5rem 1em;
                    color: ${theme.midnight};
                    background: ${theme.mono20};
                    font-size: 0.875rem;
                    font-weight: 600;
                    cursor: pointer;
                  `}
                  onClick={onBack}
                >
                  Change
                </button>
              )}
            </div>

            {mode === 'create' && (
              <>
                <label
                  css={css`
                    display: block;
                    margin-bottom: 0.35rem;
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: ${theme.midnight};
                  `}
                >
                  Create a new map
                </label>
                <input
                  type="text"
                  data-testid="upload-map-destination-map-name"
                  value={mapName}
                  onChange={(e) => setMapName(e.target.value)}
                  placeholder="eg: Site Survey - March"
                  css={css`
                    width: 100%;
                    box-sizing: border-box;
                    padding: 0.65rem 0.75rem;
                    border-radius: 8px;
                    border: 1px solid ${theme.mono30};
                    font-size: 0.9375rem;
                    margin-bottom: 1rem;
                    &:focus {
                      outline: 2px solid ${theme.deepOcean};
                      outline-offset: 0;
                    }
                  `}
                />

                <label
                  css={css`
                    display: block;
                    margin-bottom: 0.35rem;
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: ${theme.midnight};
                  `}
                >
                  Workspace
                </label>
                <Select<LabeledOption, false>
                  inputId="upload-map-destination-workspace"
                  instanceId="upload-map-destination-workspace"
                  isLoading={workspacesLoading}
                  options={workspaceOptions}
                  value={workspace}
                  onChange={(o) => {
                    if (o) {
                      setWorkspace(o);
                      setFolder(null);
                    }
                  }}
                  placeholder="Select a workspace"
                  isClearable={false}
                  menuPosition="absolute"
                  styles={selectStyles(theme)}
                />

                <label
                  css={css`
                    display: block;
                    margin-top: 1rem;
                    margin-bottom: 0.35rem;
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: ${folderSelectEnabled ? theme.midnight : theme.mono50};
                  `}
                >
                  Select a workspace folder (Optional)
                </label>
                <Select<LabeledOption, false>
                  inputId="upload-map-destination-folder"
                  instanceId="upload-map-destination-folder"
                  isLoading={foldersLoading}
                  isDisabled={!folderSelectEnabled}
                  options={folderOptions}
                  value={folder}
                  onChange={(o) => setFolder(o)}
                  placeholder="Select a workspace folder"
                  isClearable
                  menuPosition="absolute"
                  styles={selectStyles(theme)}
                />
              </>
            )}

            {mode === 'existing' && (
              <>
                <label
                  css={css`
                    display: block;
                    margin-bottom: 0.35rem;
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: ${theme.mono50};
                  `}
                >
                  Select a map
                </label>
                <AsyncSelect<MapSelectOption, false, GroupBase<MapSelectOption>>
                  cacheOptions
                  defaultOptions
                  inputId="upload-map-destination-map"
                  instanceId="upload-map-destination-map"
                  key={currentContextOrgId ?? 'no-org'}
                  loadOptions={loadMapOptions}
                  value={selectedMap}
                  onChange={(o) => {
                    setSelectedMap(o);
                    setSelectedProcessFolder(null);
                  }}
                  placeholder="Search for a map..."
                  isClearable
                  loadingMessage={() => 'Loading maps…'}
                  menuPosition="absolute"
                  styles={mapSelectStyles(theme)}
                  components={{
                    Option: MapSearchOptionRow,
                    SingleValue: MapSearchSingleValue,
                  }}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue.trim()
                      ? 'No maps match your search'
                      : 'No maps in this workspace yet'
                  }
                />
                {flowVariant === 'process' && (
                  <>
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.35rem;
                        margin-top: 1rem;
                        margin-bottom: 0.35rem;
                        flex-wrap: wrap;
                      `}
                    >
                      <label
                        css={css`
                          margin: 0;
                          font-size: 0.875rem;
                          font-weight: 500;
                          color: ${theme.mono50};
                        `}
                      >
                        Select a map folder
                      </label>
                      <InformationIndicator message={PROCESS_FOLDER_INFO_TOOLTIP} />
                    </div>
                    <Select<LabeledOption, false>
                      inputId="upload-map-destination-process-folder"
                      instanceId="upload-map-destination-process-folder"
                      isLoading={processFoldersLoading}
                      isDisabled={!selectedMap?.value || processFoldersLoading}
                      options={processFolderOptions}
                      value={selectedProcessFolder}
                      onChange={(o) => setSelectedProcessFolder(o)}
                      placeholder="Select a map folder"
                      menuPosition="absolute"
                      styles={selectStyles(theme)}
                    />
                    <div
                      role="note"
                      css={css`
                        display: flex;
                        align-items: flex-start;
                        gap: 0.65rem;
                        margin-top: 0.75rem;
                        padding: 0.75rem 0.85rem;
                        border-radius: 8px;
                        background: ${theme.mono20};
                        border: 1px solid ${theme.mono30};
                      `}
                    >
                      <span
                        css={css`
                          display: inline-flex;
                          flex-shrink: 0;
                          width: 1.35rem;
                          height: 1.35rem;
                          margin-top: 0.05rem;
                          color: ${theme.mono50};
                          & svg {
                            width: 100%;
                            height: 100%;
                          }
                        `}
                      >
                        <FolderSVG aria-hidden="true" />
                      </span>
                      <p
                        css={css`
                          margin: 0;
                          font-size: 0.8125rem;
                          line-height: 1.45;
                          color: ${theme.mono50};
                        `}
                      >
                        {PROCESS_MERGE_HINT_BOX}
                      </p>
                    </div>
                  </>
                )}
              </>
            )}

            <button
              type="button"
              data-testid="upload-map-destination-continue"
              disabled={!canContinue}
              onClick={handleContinue}
              css={css`
                width: 100%;
                margin-top: 1.5rem;
                padding: 0.75rem 1rem;
                border-radius: 8px;
                font-weight: 600;
                font-size: 0.9375rem;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.5rem;
                border: none;
                cursor: ${canContinue ? 'pointer' : 'not-allowed'};
                ${canContinue
                  ? buttonBlue(theme)
                  : css`
                      background: ${theme.mono40};
                      color: ${theme.mono0};
                    `}
              `}
            >
              <span
                css={css`
                  display: inline-flex;
                  width: 1.125rem;
                  height: 1.125rem;
                  & svg {
                    width: 100%;
                    height: 100%;
                  }
                `}
              >
                <ContinueIcon aria-hidden="true" />
              </span>
              {continueLabel}
            </button>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
